import React, { useCallback, useMemo, useState } from 'react';
import DataGrid, { Column, Pager, Paging, SearchPanel, Summary, TotalItem } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { Button as DevXButton } from 'devextreme-react/button';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from 'Common/Loader';
import { GetInwartReport } from '../services/inwartReport.service';
import StoreSelectOptions from './StoreSelectOptions';
import InwartReportModal from './InwartReportModal';

// Updated interface to match required columns
interface InwartReportData {
  id: string;
  storeName: string;
  storeCode: string;
  materialType: string;
  isSnmcc: boolean;
  isMemo:boolean;
  inwardCount: number;
}

const InwartReportDataList: React.FC = () => {
  const [selectedStore, setSelectedStore] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState<{
    id: string;          // storeId
    materialType: string;
    isMemo: boolean;
  } | null>(null);

  const [dataGridInstance, setDataGridInstance] = useState<any>(null);

  const handleViewDetails = (data: InwartReportData) => {
    setDetailModalData({
      id: selectedStore || "",
      materialType: data.materialType,
      isMemo: data.isMemo
    });
    setShowDetailsModal(true);
  };

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: "uid",
      load: async () => {
        try {
          setLoading(true);

          const params = new URLSearchParams();
          if (selectedStore) {
            params.append("StoreId", selectedStore);
          }

          const response = await GetInwartReport(params);

          const dataWithId = (response.data ?? []).map((item: any, index: number) => ({
            ...item,
            uid: `${Date.now()}_${index}`
          }));

          return {
            data: dataWithId,
            totalCount: dataWithId.length
          };
        } catch (error) {
          console.error("Data loading error:", error);
          return { data: [], totalCount: 0 };
        } finally {
          setLoading(false);
        }
      }
    });
  }, [selectedStore]);

  const renderSerialNumber = useCallback((cellData: any) => {
    const rowIndex = cellData.rowIndex + 1 + cellData.component.pageIndex() * cellData.component.pageSize();
    return <span>{rowIndex}</span>;
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!dataGridInstance) return;

    setLoading(true);
    try {
      const dataSource = dataGridInstance.getDataSource();
      dataSource.load().then((data: any[]) => {
        // Updated columns for Excel export
        const columns = [
          { key: 'storeName', name: 'Store Name' },
          { key: 'storeCode', name: 'Store Code' },
          { key: 'materialType', name: 'Material Type' },
          { key: 'isSnmcc', name: 'Is Memo' },
          { key: 'inwardCount', name: 'Inward Count' }
        ];

        let excelData: any[] = [];

        if (data.length > 0) {
          excelData.push(columns.reduce((obj, col) => ({
            ...obj,
            [col.name]: col.name
          }), {}));

          data.forEach(item => {
            excelData.push({
              'Store Name': item.storeName || '',
              'Store Code': item.storeCode || '',
              'Material Type': item.materialType || '',
              'Is Memo': item.isMemo ? 'True' : 'False',
              'Inward Count': item.inwardCount || 0
            });
          });
        } else {
          excelData.push(columns.reduce((obj, col) => ({
            ...obj,
            [col.name]: col.name
          }), {}));
        }

        const worksheet = XLSX.utils.json_to_sheet(excelData, { skipHeader: true });

        const wscols = columns.map(col => ({ width: col.name.length + 5 }));
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inward Report");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });

        saveAs(blob, `Inward_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setLoading(false);
    }
  }, [dataGridInstance]);

  const handleStoreChangeFromChild = (value: string) => {
    setSelectedStore(value || undefined);
  };
  const isMainStore = localStorage.getItem('isMainStore')
  return (
    <React.Fragment>
      {loading && <Loader />}

      <div className="page-content">
        <div className="container-fluid">
          <Card>
            <Card.Header className='d-flex justify-content-between align-items-center'>
              <h5 className='m-0'>Inward Stock Report</h5>
              <DevXButton
                icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                hint="Export to Excel"
                onClick={handleExportExcel}
              />
            </Card.Header>
            <div style={{ marginLeft: '20px', paddingTop: '10px' }}>
              {isMainStore === 'true' && (
                <StoreSelectOptions onStoreChange={handleStoreChangeFromChild} />
              )}
            </div>
            <Card.Body>
              <DataGrid
                dataSource={dataSource}
                showRowLines={true}
                keyExpr="id"
                showBorders={true}
                columnAutoWidth={true}
                onInitialized={(e) => setDataGridInstance(e.component)}
                onContentReady={(e) => {
                  const hasData = e.component.totalCount() > 0;
                  e.component.option("pager.visible", hasData);
                  e.component.option("paging.visible", hasData);
                }}
              >
                <Pager
                  showPageSizeSelector={true}
                  allowedPageSizes={[10, 25, 50, 100]}
                  showNavigationButtons={true}
                  displayMode="full"
                  showInfo={true}
                />
                <Summary>
                  <TotalItem />
                </Summary>
                <Paging defaultPageSize={10} />
                {/* <SearchPanel visible width={240} placeholder="Search..." /> */}

                <Column
                  caption="Action"
                  dataType="string"
                  cellRender={(cellData) => (
                    <Button
                      variant="black"
                      onClick={() => {
                        console.log(cellData.data)
                        handleViewDetails(cellData.data)
                      }
                      }
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                  )}
                  allowFiltering={false}
                />
                <Column
                  caption="Sr.No."
                  dataType="number"
                  alignment="center"
                  cellRender={renderSerialNumber}
                />

                <Column allowFiltering={true} dataField="storeName" caption="Store Name" dataType="string" />
                <Column allowFiltering={true} dataField="storeCode" caption="Store Code" dataType="string" />
                <Column allowFiltering={true} dataField="materialType" caption="Material Type" dataType="string" />

                <Column
                  allowFiltering={true}
                  dataField="isMemo"
                  caption="Is Memo"
                  dataType="boolean"
                  cellRender={(cellData) => (cellData.value ? "True" : "False")}
                />

                <Column allowFiltering={true} dataField="inwardCount" caption="Inward Count" dataType="number" />
              </DataGrid>
            </Card.Body>
          </Card>
        </div>
      </div>

      {showDetailsModal && detailModalData && (
        <InwartReportModal
          storeId={detailModalData.id}
          materialType={detailModalData.materialType}
          isMemo={detailModalData.isMemo}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </React.Fragment>
  );
};

export default InwartReportDataList;
